import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, styled, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { IGenericAutoComplete } from "../../_common/interface/autocomplete/iStandardAutocomplete";
import { IDropdownData } from "../../_common/interface/dropdown/iDropdownData.interface";
import HighlightText from '../text/highlight.text';

interface IStandardExtAutocomplete extends IGenericAutoComplete {
    itemsLoader: (search: string) => Promise<any>;
}
const StyledOption = styled('li')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%', // Ensures the list item spans full width
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',

    '& .title': {
        fontWeight: 500,
        fontSize: '1rem',
        textAlign: 'left',
        width: '100%',
    },

    '& .description': {
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        textAlign: 'left',
        width: '100%',
    },

    '& .ticket': {
        marginLeft: "100px",
        paddingLeft: "16px",
        // borderLeft: "1px solid silver"
    },
}));
export default function GlobalSearchAutoComplete(params: IStandardExtAutocomplete) {

    const navigate = useNavigate();

    const [value, setValue] = useState<IDropdownData | null>();

    const [inputValue, setInputValue] = useState("");
    const [searchValue, setSearchValue] = useState("");

    const [isLoading, setLoading] = useState(false);
    const [items, setItems] = useState<Array<any>>([]);
    const [initaialLoad, setInitaialLoad] = useState(false);

    const [excludedIds, setExcludedIds] = useState<Array<number | string>>(
        params.excludedIds || []
    );

    useEffect(() => {
        if (!initaialLoad) {
            setInitaialLoad(true);
            return;
        }

        const timeout = setTimeout(() => {
            setSearchValue((__filter: any) => {
                if (__filter === inputValue) return __filter;
                return inputValue;
            });
        }, 300);

        return () => clearTimeout(timeout);
    }, [inputValue]);

    useEffect(() => {
        if (params.excludedIds != excludedIds) {
            setExcludedIds(params.excludedIds ? params.excludedIds : []);
        }
    }, [params.excludedIds]);

    useEffect(() => {
        if (searchValue)
            fetchItems();
        else if (items && items.length) setItems([]);
    }, [searchValue]);

    useEffect(() => {
        if (items && items.length > 0) {
            changeInput(items.find((_item: any) => _item.id === params.value));
        } else if (
            ((!items || items.length === 0) && value) ||
            ((!params.value || params.value === 0) && value)
        ) {
            changeInput(null);
        }
    }, [items, params.value]);

    function fetchItems() {
        setLoading(true);
        return params
            .itemsLoader(inputValue)
            .then(
                (json) => {
                    if (!json || json.length < 1) {
                        setItems([]);
                    } else {
                        setItems(json);
                    }
                },
                (err) => {
                    setItems([]);
                }
            )
            .finally(() => setLoading(false));
    }

    function changeInput(newValue: any) {
        newValue = newValue ? newValue : null;
        if (newValue === value) return;
        setValue(newValue);

        params.onChange?.({ target: { name: params.name, value: newValue?.id } }, newValue, params.name);
    }

    const toolbar = params.toolbarCompact === true;

    const autocompleteSx = toolbar
        ? {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              borderRadius: 1,
              color: "white !important",
              marginRight: 0,
              marginTop: 0,
              marginBottom: 0,
              width: "100%",
              maxWidth: "100%",
              alignSelf: "center",
              "& .MuiOutlinedInput-root": {
                  minHeight: 36,
                  height: 36,
                  paddingTop: 0,
                  paddingBottom: 0,
                  fontSize: "0.8125rem",
              },
              "& .MuiInputBase-input": {
                  border: "none",
                  color: "white !important",
                  py: "6px",
                  fontSize: "0.8125rem",
              },
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& button": { color: "white !important" },
          }
        : {
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              borderRadius: 1,
              color: "white !important",
              marginRight: 2,
              width: "600px",
              "& .MuiInputBase-input": {
                  border: "none",
                  color: "white !important",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                  color: "white !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                  color: "white !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                  color: "white !important",
              },
              "& button": {
                  color: "white !important",
              },
          };

    const optionCompactSx = toolbar
        ? {
              py: 0.65,
              "& .title": { fontSize: "0.8125rem", fontWeight: 600 },
              "& .description": { fontSize: "0.72rem", lineHeight: 1.35 },
              "& .ticket": { marginLeft: "6px", paddingLeft: "8px" },
          }
        : undefined;

    return (
        <>
            <Autocomplete
                color='white'
                fullWidth={true}
                sx={autocompleteSx}
                slotProps={{
                    paper: toolbar
                        ? {
                              sx: {
                                  maxHeight: 280,
                                  maxWidth: "min(calc(100vw - 24px), 440px)",
                                  mt: 0.5,
                              },
                          }
                        : undefined,
                    listbox: toolbar
                        ? {
                              sx: {
                                  maxHeight: 260,
                                  py: 0.5,
                                  "& .MuiAutocomplete-option": { minHeight: "auto" },
                              },
                          }
                        : undefined,
                    popper: toolbar
                        ? {
                              placement: "bottom-start",
                              modifiers: [
                                  { name: "offset", options: { offset: [0, 6] } },
                                  { name: "preventOverflow", options: { padding: 8, boundary: "clippingParents" } },
                              ],
                          }
                        : undefined,
                }}
                open={params.open}
                onOpen={params.onOpen}
                id="tags-standard"
                disableClearable={params.disableClearable ? true : false}
                options={excludedIds && excludedIds.length ? items.filter(m => m.id === value || !excludedIds.some(r => r === m.id.toString())) : items}
                filterOptions={(options) => options}
                getOptionLabel={(option) => (`${option.ticketId === 0 ? option.caseId : option.ticketReference}`)}
                renderOption={(props, option) => (
                    <>
                        {option.ticketId === 0 &&
                            <StyledOption {...props} sx={optionCompactSx}>
                                <span className="title">Case [<HighlightText text={option.caseId} highlight={searchValue} />] <HighlightText text={option.clientName} highlight={searchValue} /></span>
                                <span className="description">{option.clientPoc} - {option.clientReference}</span>
                            </StyledOption>
                        }

                        {option.ticketId !== 0 &&
                            <StyledOption {...props} sx={optionCompactSx}>
                                <span className="title ticket"> Ticket [<HighlightText text={option.ticketReference} highlight={searchValue} />] <HighlightText text={option.ticketSubject} highlight={searchValue} /> </span>
                                <span className="description ticket"> <HighlightText text={option.ticketTypeString} highlight={searchValue} /> - <HighlightText text={option.ticketStatus} highlight={searchValue} /> - <HighlightText text={`${option.subjectAddressLine1} ${option.subjectAddressLine2} ${option.subjectCity} ${option.subjectCountry} ${option.subjectEirCode}`} highlight={searchValue} /></span>
                            </StyledOption>
                        }
                    </>
                )}
                value={value || null}
                onChange={(event: any, option: any) => {
                    let url = option.ticketId === 0 ? "Cases/Manage/" + option.caseId : "Case/" + option.caseId + "/Ticket/" + option.ticketId + "/" + option.ticketType;
                    navigate(url)
                }}
                inputValue={inputValue || ''}
                onInputChange={(event: any, newInputValue: any) => {
                    setInputValue(newInputValue);
                }}
                disabled={params.disabled}
                loading={isLoading}
                loadingText="Loading results..."
                noOptionsText={inputValue ? "No results found" : "Start typing to search. Use ',' to multi-query search. Search ignores special characters"}
                renderInput={(props) => (
                    <TextField
                        {...props}
                        size={toolbar || params.isFilter ? "small" : undefined}
                        variant={params.variant || "outlined"}
                        value={inputValue || ''}
                        sx={
                            toolbar
                                ? { pl: 0.5, color: "white !important", width: "100%" }
                                : { paddingLeft: "40px", color: "white !important", width: "calc(100% - 40px)" }
                        }
                        required={params.required}
                        hiddenLabel={toolbar}
                        label={
                            params.isInsideCell
                                ? ""
                                : toolbar
                                  ? ""
                                  : (
                                        <>
                                            {params.label}
                                            <SearchIcon style={{ color: "white" }} className="myIcon" fontSize="small" color="disabled" />
                                        </>
                                    )
                        }
                        placeholder={toolbar ? "Search cases & tickets…" : params.label}
                        InputProps={{
                            ...props.InputProps,
                            startAdornment: toolbar ? (
                                <>
                                    <SearchIcon sx={{ color: "rgba(255,255,255,0.85)", mr: 0.5, fontSize: 18 }} />
                                    {props.InputProps?.startAdornment}
                                </>
                            ) : (
                                props.InputProps?.startAdornment
                            ),
                        }}
                        InputLabelProps={{
                            shrink: !!value,
                        }}
                    />
                )}
            />
        </>
    )

}
